/**
 * NarrativeNotes — block "Finished product quality after processing": danh sách
 * item Graded (thành phẩm current PO có actualMt>0) + 2 cột Note/Standard nhập tay.
 *
 * Note/Standard persist ở `po.compare_edit` ({items:{<code>:{note?,standardized?}}})
 * qua `useSaveBatchReportMutation` — KHÔNG seed lazy, empty-branch giữ '' để Save
 * xoá đúng khi user clear ([[feedback_copy_seed_readonly_from_row]]). Số weighted-avg
 * /item (MC/BB/FM/S18/S16/S13) đọc từ narrative BE (cùng nguồn comparison endpoint).
 *
 * Dùng ở tab Production Analysis (chuyển từ Comparison sang 2026-06-19).
 */
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import { Box, Button, CircularProgress, TextField, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import { useSaveBatchReportMutation, type BatchReportComparison } from '../../../../store/api/factoryApi'
import { BlockTitle, NumText } from './reportUi'
import { COLORS, sectionSubtitleSx } from './styleTokens'
import { FilterTable, type FilterCol } from './FilterTable'

type NarrativeRow = BatchReportComparison['narrative'][number]

export default function NarrativeNotes({
  rows,
  factoryCode,
  poNo,
  canEdit,
  onSaved,
}: {
  rows: NarrativeRow[]
  factoryCode: string
  poNo: string
  canEdit: boolean
  onSaved: () => void
}) {
  const [saveBatchReport, { isLoading: saving }] = useSaveBatchReportMutation()
  // Draft note/standardized theo item. Reset khi rows đổi (đổi PO / sau khi save
  // refetch) — seed từ giá trị server (đã persist).
  const [draft, setDraft] = useState<Record<string, { note: string; standardized: string }>>({})
  const [dirty, setDirty] = useState(false)
  useEffect(() => {
    const seed: Record<string, { note: string; standardized: string }> = {}
    rows.forEach((r) => {
      seed[r.item] = { note: r.note ?? '', standardized: r.standardized ?? '' }
    })
    setDraft(seed)
    setDirty(false)
  }, [rows])

  const setField = (item: string, field: 'note' | 'standardized', v: string) => {
    setDraft((prev) => ({ ...prev, [item]: { ...(prev[item] ?? { note: '', standardized: '' }), [field]: v } }))
    setDirty(true)
  }
  const handleSave = async () => {
    const items: Record<string, { note: string; standardized: string }> = {}
    rows.forEach((r) => {
      const d = draft[r.item] ?? { note: '', standardized: '' }
      items[r.item] = { note: d.note.trim(), standardized: d.standardized.trim() }
    })
    try {
      await saveBatchReport({ factoryCode, poNo, body: { compareEdit: { items } } }).unwrap()
      setDirty(false)
      onSaved()
    } catch {
      /* lỗi đã hiện qua RTK; giữ draft để user thử lại */
    }
  }

  const noteCell = (v: string | null | undefined) => (
    <Box component="span" sx={{ display: 'block', whiteSpace: 'pre-line', overflowWrap: 'anywhere', color: COLORS.textMuted }}>
      {v || '—'}
    </Box>
  )
  const editCell = (item: string, field: 'note' | 'standardized') => (
    <TextField
      value={draft[item]?.[field] ?? ''}
      onChange={(e) => setField(item, field, e.target.value)}
      multiline
      size="small"
      placeholder="—"
      fullWidth
      variant="standard"
      InputProps={{ disableUnderline: false }}
      sx={{ '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.74rem' }, color: COLORS.text } }}
    />
  )
  const renderNote = (r: NarrativeRow) => (canEdit ? editCell(r.item, 'note') : noteCell(r.note))
  const renderStd = (r: NarrativeRow) => (canEdit ? editCell(r.item, 'standardized') : noteCell(r.standardized))
  const cols: FilterCol<NarrativeRow>[] = [
    {
      key: 'item',
      label: 'Item',
      width: 110,
      frozen: true,
      value: (r) => r.item,
      render: (r) => <Box component="span" className="app-table-cell-wrap" sx={{ fontWeight: 600 }}>{r.item}</Box>,
    },
    // Số weighted-avg/item của thành phẩm Graded (gộp từ block "Finished product
    // quality after processing" — read-only).
    { key: 'mc', label: 'MC', width: 76, align: 'right', numeric: true, value: (r) => r.mc, render: (r) => <NumText n={r.mc} d={2} /> },
    { key: 'bb', label: 'BB', width: 76, align: 'right', numeric: true, value: (r) => r.bb, render: (r) => <NumText n={r.bb} d={2} /> },
    { key: 'fm', label: 'FM', width: 76, align: 'right', numeric: true, value: (r) => r.fm, render: (r) => <NumText n={r.fm} d={2} /> },
    { key: 's18', label: 'S18', width: 76, align: 'right', numeric: true, value: (r) => r.s18, render: (r) => <NumText n={r.s18} d={2} /> },
    { key: 's16', label: 'S16', width: 76, align: 'right', numeric: true, value: (r) => r.s16, render: (r) => <NumText n={r.s16} d={2} /> },
    { key: 's13', label: 'S13', width: 76, align: 'right', numeric: true, value: (r) => r.s13, render: (r) => <NumText n={r.s13} d={2} /> },
    { key: 'note', label: 'Note', width: 280, value: (r) => draft[r.item]?.note ?? r.note ?? '', render: renderNote },
    { key: 'standardized', label: 'Standard', width: 280, value: (r) => draft[r.item]?.standardized ?? r.standardized ?? '', render: renderStd },
  ]
  const saveBtn = canEdit ? (
    <Tooltip title={!dirty ? 'No changes to save' : ''}>
      <span>
        <Button
          variant="contained"
          size="small"
          color="success"
          disabled={!dirty || saving}
          onClick={handleSave}
          startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
          sx={{ textTransform: 'none', fontSize: '0.7rem', fontWeight: 700, py: 0.2, px: 1, minHeight: 0 }}
        >
          {saving ? 'Saving…' : 'Save notes'}
        </Button>
      </span>
    </Tooltip>
  ) : undefined
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <BlockTitle>Quality information</BlockTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Typography sx={{ ...sectionSubtitleSx, mb: 0, display: { xs: 'none', sm: 'block' } }}>
            Graded items from the batch out-turn. Note / Standard are analyst-entered.
          </Typography>
          {saveBtn}
        </Box>
      </Box>
      <FilterTable storageKey="production-analysis.narrative" columns={cols} rows={rows} rowKey={(r) => r.item} searchPlaceholder="Search items…" />
    </Box>
  )
}
